// External imports
import React, { useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import $ from 'jquery';
// Internal imports
import { publicRoutes, privateRoutes } from '~/app/Router/Routes.js';
import PrivateRoute from '~/app/Components/Auth/PrivateRoute/PrivateRoute.jsx';
import GlobalSnackbar from '#/Common/GlobalSnackbar.jsx';
import GenericErrorModal from '#/Common/GenericErrorModal.jsx';
import CLOIdleTimer from '#/Common/CLOIdleTimer.jsx';
import HeaderBar from '~/app/Components/HeaderBar/HeaderBar.jsx';
import Sidebar from '~/app/Components/Common/Sidebar/Sidebar.jsx';
import AppVersion from './AppVersion';
import { EXCLUDE_FLEX } from '~/app/constants.js';
import Footer from '~/app/Components/Footer/Footer.jsx';
import MaintenanceMode from '#/Common/MaintenanceRoutineMsg/MaintenanceMode.jsx';
import { NISTAuditLog } from '~/app/Utility/clientNISTLogger.js';
import { NIST_LOG_EVENTS } from '~/app/Utility/clientNISTConstants.js';
import { getPageName } from '~/app/Utility/general';
import { actions as appActions } from '~/app/redux/modules/app.js';
// Styling imports
import { MuiThemeProvider } from '@material-ui/core/styles';
import appTheme from '~/themes/GenericTheme.jsx';
import '~/app/styles/App.css'; // defines paths to all external CSS

/**
 * This is the main entry component which acts as a container for all other components.
 *
 * @component
 */
const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  //SSE, currently only used for docker
  useEffect(() => {
    if (process.env.DOCKER_MODE !== 'true') {
      return;
    }
    //create connection to event channel on backend
    const events = new EventSource(`${XLINK_PUB_URL}/sse/events`);

    events.onopen = () => {
      console.log('SSE OPEN');
    };

    events.onerror = err => {
      console.log('SSE ERROR', err);
    };

    events.onmessage = event => {
      dispatch(
        appActions.showSnackbarMessage(event.data, 'info', 10000, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
    };

    return () => {
      events.close();
    };
  }, [process.env.DOCKER_MODE]);

  useEffect(() => {
    handleTitle();
    document.addEventListener('keydown', stopKeyZoom);

    return () => {
      // Cleanup event listener.
      document.removeEventListener('keydown', stopKeyZoom);
    };
  }, []);

  // Anytime we router to a new component page, we want to log the action for NIST compliance
  useEffect(() => {
    const pageName = getPageName(location.pathname);

    // NIST - Logging the action of a the user viewing a page
    NISTAuditLog(
      NIST_LOG_EVENTS.VIEWING_PAGE.key,
      `User is viewing the ${pageName} page`,
      `View ${pageName} Page`,
    );
  }, [location.pathname]);

  /**
   * Change browser tab title to My Online Tax Office or Crosslink Online
   * base on cobrand URL myonlinetaxoffice.com.
   */
  const handleTitle = () => {
    // disable zoom
    document.firstElementChild.style.zoom = 'reset';

    let title = 'Crosslink Online';
    if (window.location.href.includes('myonlinetaxoffice')) {
      title = 'My Online Tax Office';
    }

    $(document).prop('title', title);
  };

  /**
   * Handle disabling zoom with control keys.
   */
  const stopKeyZoom = event => {
    if (event.ctrlKey && [48, 61, 96, 107, 109, 187, 189].indexOf(event.keyCode) > -1) {
      event.preventDefault();
    }
  };

  return (
    <div className="App">
      <MuiThemeProvider theme={appTheme}>
        <HeaderBar />
        <CLOIdleTimer />
        <GlobalSnackbar />
        <AppVersion />
        <GenericErrorModal />
        <MaintenanceMode />
        <div className={`${!EXCLUDE_FLEX.some(v => location.pathname === v) && 'app-flex'}`}>
          <Sidebar />
          <Switch>
            {publicRoutes.map(({ path, exact, Component }, key) => (
              <Route
                path={path}
                key={key}
                exact={exact}
                render={props => <Component {...props} />}
              />
            ))}
            {privateRoutes.map(
              ({ path, exact, Component, navigation, requiresPermission, requiredRole }, key) => (
                <PrivateRoute
                  path={path}
                  key={key}
                  exact={exact}
                  requiresPermission={requiresPermission}
                  requiredRole={requiredRole}
                  render={props => <Component {...props} navigation={navigation} />}
                />
              ),
            )}
          </Switch>
          <Footer />
        </div>
      </MuiThemeProvider>
    </div>
  );
};

export default React.memo(App);
